import {ReactElement, useMemo} from "react";
import {Account} from "../../../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {parseNumber} from "../../../../../utils/string-helpers";
import {ensureNotObject, keyify} from "../common";
import * as common from "../styles/common.module.scss";
import {renderDistributionMethod, renderMonthlyAvgBasePeriod} from "./common";
import * as styles from "./styles/RenderPendingUpdates.module.scss";

function renderLookback(lookback: any): string {
    if(typeof lookback !== "number" || lookback <= 0) {
        return "";
    }

    return `Lookback Period: ${lookback} month${lookback > 1 ? "s" : ""}`;
}

function renderAccountPercentage(accPercentage: Record<string, any>, accountsMap: Record<string, Account>): ReactElement {
    if(!accPercentage) {
        return <></>;
    }

    const accountId = accPercentage["accountId"];
    if(!accountId) {
        return <></>;
    }

    let name = accountId;
    const account = accountsMap[accountId];
    if(account) {
        if(account.name) {
            name = account.name;
        } else if(account.accountNumber) {
            name = `${account.accountNumber} ${account.name}`;
        }
    }

    const lookback = accPercentage["lookbackPeriod"];
    return <div>
        <div>Source Account: {ensureNotObject(name)}</div>
        {typeof lookback === "number" && lookback > 0 ? <div>{renderLookback(lookback)}</div> : <></>}
    </div>;
}

function renderAccountPercentageAugments(accPercentageAugments: Record<string, any>): ReactElement {
    if(!accPercentageAugments) {
        return <></>;
    }

    const minVal = accPercentageAugments["minVal"];
    const maxVal = accPercentageAugments["maxVal"];

    if((minVal === undefined || minVal === null) && (maxVal === undefined || maxVal === null)) {
        return <></>;
    }

    return <>
        <div>Minimum: {ensureNotObject(minVal)}</div>
        <div>Maximum: {ensureNotObject(maxVal)}</div>
    </>;
}

function renderRevenue(revenue: Record<string, any>): ReactElement {
    if(!revenue) {
        return <></>;
    }

    const type = revenue["revenueType"];
    return <>{ensureNotObject(type)}</>;
}

function renderWorksheet(worksheet: Record<string, any>): ReactElement {
    if(!worksheet) {
        return <></>;
    }

    const description = worksheet["description"];
    if(typeof description !== "string") {
        return <></>;
    }

    return <>{description}</>;
}

function renderOperational(operational: Record<string, any>, metricTypes: {id: string, name: string}[]): ReactElement {
    if(!operational) {
        return <></>;
    }

    const sourceId = operational["sourceMetricId"];
    const metricType = metricTypes.find(mt => mt.id === sourceId);
    const lookback = operational["lookbackPeriod"];
    const name = operational["name"];
    const newName = operational["newName"];
    const cumSum = operational["cumSum"];
    const nonProrated = operational["nonProrated"];
    const minValue = operational["minValue"];
    const maxValue = operational["maxValue"];

    const minValueAsNumOrNull = parseNumber(minValue);
    const maxValueAsNumOrNull = parseNumber(maxValue);

    return <div>
        {name && !newName && <div>Name: {ensureNotObject(name)}</div>}
        {name && newName && <div>Name: {ensureNotObject(name)} -&gt; {ensureNotObject(newName)}</div>}
        {metricType && <div>Source Metric: {ensureNotObject(metricType.name)}</div>}
        {(typeof lookback === "number" && lookback > 0) ? <div>{renderLookback(lookback)}</div> : <></>}
        {cumSum && <div>Has Ramp Up</div>}
        {nonProrated === true && <div>Not Prorated</div>}
        <div>Min Value: {minValueAsNumOrNull ?? "None"}</div>
        <div>Max Value: {maxValueAsNumOrNull ?? "None"}</div>
    </div>;
}

function renderPayroll(payroll: Record<string, any>[]): ReactElement {
    if(!payroll || payroll.length === 0) {
        return <></>;
    }

    const items: ReactElement[] = [];
    for(const p of payroll) {
        const itemType = p["itemType"];
        const positions = p["positions"];

        if(!itemType) {
            continue;
        }

        const names: string[] = [];
        if(Array.isArray(positions) && positions.length !== 0) {
            for(const pos of positions) {
                if(typeof pos === "object" && pos["name"]) {
                    names.push(pos["name"]);
                }
            }
        }

        items.push(
                <div>
                    <div>Type: {ensureNotObject(itemType)}</div>
                    {names.length > 0 &&
                            <ul>
                                {names.map(n => <li key={keyify(n)}>{ensureNotObject(n)}</li>)}
                            </ul>
                    }
                </div>
        );
    }

    if(items.length === 0) {
        return <></>;
    }

    return <div>
        {items}
    </div>;
}

function renderCustom(custom: unknown): ReactElement {
    if(typeof custom !== "string") {
        return <></>;
    }

    return <div>{custom}</div>;
}

function renderPercentGrowth(percentGrowth: Record<string, any>): ReactElement {
    if(!percentGrowth) {
        return <></>;
    }

    const growth = percentGrowth["percentGrowthAdjustmentValue"];
    const basePeriod = percentGrowth["percentGrowthBasePeriod"];

    if(!basePeriod) {
        return <></>;
    }

    return <div>
        <div>Growth: {ensureNotObject(growth)}%</div>
        <div>Base Period: {ensureNotObject(basePeriod)}</div>
    </div>;
}

function renderYearlyAverage(yearlyAverage: Record<string, any>): ReactElement {
    if(!yearlyAverage) {
        return <></>;
    }

    const monthlyAdjustmentType = yearlyAverage["monthlyAdjustmentType"];
    const monthlyAdjustmentValue = yearlyAverage["monthlyAdjustmentValue"];
    const periodBase = yearlyAverage["monthlyAverageBasePeriod"];

    if(!monthlyAdjustmentType || !periodBase) {
        return <></>;
    }

    const periodEnd = yearlyAverage["lookbackPeriodEnd"];
    const periodStart = yearlyAverage["lookbackPeriodStart"];

    return <div>
        <div>Yearly Adjustment: {ensureNotObject(monthlyAdjustmentValue)} {ensureNotObject(monthlyAdjustmentType)}</div>
        <div>
            {renderMonthlyAvgBasePeriod(periodBase)}
            {periodStart && <div className={styles.indentedChild}>From: {ensureNotObject(periodStart)}</div>}
            {periodEnd && <div className={styles.indentedChild}>To: {ensureNotObject(periodEnd)}</div>}
        </div>
    </div>;
}

function renderMonthlyAverage(monthlyAverage: Record<string, any>): ReactElement {
    if(!monthlyAverage) {
        return <></>;
    }

    const monthlyAdjustmentType = monthlyAverage["monthlyAdjustmentType"];
    const monthlyAdjustmentValue = monthlyAverage["monthlyAdjustmentValue"];
    const periodBase = monthlyAverage["monthlyAverageBasePeriod"];

    if(!monthlyAdjustmentType || !periodBase) {
        return <></>;
    }

    const periodEnd = monthlyAverage["lookbackPeriodEnd"];
    const periodStart = monthlyAverage["lookbackPeriodStart"];

    return <div>
        <div>Monthly Adjustment: {ensureNotObject(monthlyAdjustmentValue)} {ensureNotObject(monthlyAdjustmentType)}</div>
        <div>
            {renderMonthlyAvgBasePeriod(periodBase)}
            {periodStart && <div className={styles.indentedChild}>From: {ensureNotObject(periodStart)}</div>}
            {periodEnd && <div className={styles.indentedChild}>To: {ensureNotObject(periodEnd)}</div>}
        </div>
    </div>;
}

function renderAnnualTargetValue(annualAmount: Record<string, any>): ReactElement {
    if(!annualAmount) {
        return <></>;
    }

    const amount = annualAmount["annualTargetValueManualEntry"];
    const distributionMethod = annualAmount["distributionMethod"];

    if(typeof amount !== "number" || !distributionMethod) {
        return <></>;
    }

    return <div>
        <div>Annual Amount: {amount}</div>
        <div>Distribution Method: {renderDistributionMethod(distributionMethod)}</div>
    </div>;
}

function renderOperationalMonthlyAverage(operationalMonthlyAverage: Record<string, any>): ReactElement {
    if(!operationalMonthlyAverage) {
        return <></>;
    }

    const sourceMetric = operationalMonthlyAverage["sourceMetric"];
    const startMonth = operationalMonthlyAverage["startMonth"];
    const endMonth = operationalMonthlyAverage["endMonth"];
    const monthlyGrowth = operationalMonthlyAverage["monthlyGrowth"];
    const yearlyGrowth = operationalMonthlyAverage["yearlyGrowth"];
    const customIncrease = operationalMonthlyAverage["customIncrease"];

    if(!sourceMetric || !startMonth || !endMonth) {
        return <></>;
    }

    return <div>
        <div>Source Metric: {ensureNotObject(sourceMetric)}</div>
        <div>Start Month: {ensureNotObject(startMonth)}</div>
        <div>End Month: {ensureNotObject(endMonth)}</div>
        {monthlyGrowth ? <div>Monthly Growth: {ensureNotObject(monthlyGrowth)}%</div> : <></>}
        {yearlyGrowth ? <div>Yearly Growth: {ensureNotObject(yearlyGrowth)}%</div> : <></>}
        {customIncrease ? <div>Custom Growth</div> : <></>}
        {!monthlyGrowth && !yearlyGrowth && !customIncrease ? <div>No Growth</div> : <></>}
    </div>;
}

function renderDrivers(drivers: Record<string, any>, accountsMap: Record<string, Account>, sourceMetrics: {id: string, name: string}[]): ReactElement[] {
    if(!drivers) {
        return [];
    }

    const accounts = drivers["account"];
    const payroll = drivers["payroll"];
    const revenue = drivers["revenue"];
    const worksheet = drivers["worksheet"];
    const customItems = drivers["customItems"];
    const operational = drivers["operational"];
    const percentGrowth = drivers["percentGrowth"];
    const yearlyAverage = drivers["yearlyAverage"];
    const monthlyAverage = drivers["monthlyAverage"];
    const annualTargetValue = drivers["annualTargetValue"];
    const accountPercentageAugment = drivers["accountPercentageAugment"];
    const operationalMonthlyAverage = drivers["operationalMonthlyAverage"];

    const renders: ReactElement[] = [];
    if(Array.isArray(accounts) && accounts.length > 0) {
        renders.push(<div className={common.section}>
            <div>Account Percentage</div>
            <div className={styles.driverData}>{accounts.map(acc => renderAccountPercentage(acc, accountsMap))}</div>
        </div>);
    }

    if(typeof accountPercentageAugment === "object" && accountPercentageAugment) {
        renders.push(<div className={common.section}>
            <div>Account Percentage Min/Max</div>
            <div className={styles.driverData}>{renderAccountPercentageAugments(accountPercentageAugment)}</div>
        </div>);
    }

    if(Array.isArray(payroll) && payroll.length > 0) {
        renders.push(<div className={common.section}>
            <div>Payroll</div>
            <div className={styles.driverData}>{renderPayroll(payroll)}</div>
        </div>);
    }

    if(Array.isArray(revenue) && revenue.length > 0) {
        // There is only ever one revenue driver, but
        // for some reason we decided to use an array. :/
        renders.push(<div className={common.section}>
            <div>Revenue</div>
            <div className={styles.driverData}>{revenue.map(r => renderRevenue(r))}</div>
        </div>);
    }

    if(Array.isArray(worksheet) && worksheet.length > 0) {
        renders.push(<div className={common.section}>
            <div>Line Items</div>
            <div className={styles.driverData}>{worksheet.map(w => renderWorksheet(w))}</div>
        </div>);
    }

    if(Array.isArray(customItems) && customItems.length > 0) {
        renders.push(<div className={common.section}>
            <div>Custom</div>
            <div className={styles.driverData}>{customItems.map(ci => renderCustom(ci))}</div>
        </div>);
    }

    if(Array.isArray(operational) && operational.length > 0) {
        renders.push(<div className={common.section}>
            <div>Operational</div>
            <div className={styles.driverData}>{operational.map(o => renderOperational(o, sourceMetrics))}</div>
        </div>);
    }

    if(Array.isArray(percentGrowth) && percentGrowth.length > 0) {
        // There is only ever one percent growth driver, but
        // for some reason we decided to use an array. :/
        renders.push(<div className={common.section}>
            <div>Percent Growth</div>
            <div className={styles.driverData}>{percentGrowth.map(pg => renderPercentGrowth(pg))}</div>
        </div>);
    }

    if(Array.isArray(yearlyAverage) && yearlyAverage.length > 0) {
        // There is only ever one yearly average driver, but
        // for some reason we decided to use an array. :/
        renders.push(<div className={common.section}>
            <div>Yearly Average</div>
            <div className={styles.driverData}>{yearlyAverage.map(ya => renderYearlyAverage(ya))}</div>
        </div>);
    }

    if(Array.isArray(monthlyAverage) && monthlyAverage.length > 0) {
        // There is only ever one monthly average driver, but
        // for some reason we decided to use an array. :/
        renders.push(<div className={common.section}>
            <div>Monthly Average</div>
            <div className={styles.driverData}>{monthlyAverage.map(ma => renderMonthlyAverage(ma))}</div>
        </div>);
    }

    if(Array.isArray(annualTargetValue) && annualTargetValue.length > 0) {
        // There is only ever one annual target value driver, but
        // for some reason we decided to use an array. :/
        renders.push(<div className={common.section}>
            <div>Annual Target Value</div>
            <div className={styles.driverData}>{annualTargetValue.map(atv => renderAnnualTargetValue(atv))}</div>
        </div>);
    }

    if(typeof operationalMonthlyAverage === "object" && operationalMonthlyAverage) {
        renders.push(<div className={common.section}>
            <div>Operational Monthly Average</div>
            <div className={styles.driverData}>{renderOperationalMonthlyAverage(operationalMonthlyAverage)}</div>
        </div>);
    }

    return renders;
}

export interface RenderPendingUpdatesProps {
    accounts: Account[];
    operationalMetricTypes: {id: string, name: string}[];
    pendingUpdates: Record<string, any>;
}

export function RenderPendingUpdates(props: RenderPendingUpdatesProps): ReactElement {
    const accountsMap = useMemo(() => {
        return props.accounts.toIdMap(a => a.id);
    }, [props.accounts]);

    const pendingUpdates = props.pendingUpdates;

    const add = pendingUpdates["add"];
    const remove = pendingUpdates["remove"];
    const update = pendingUpdates["update"];

    const addRenders = renderDrivers(add, accountsMap, props.operationalMetricTypes);
    const removeRenders = renderDrivers(remove, accountsMap, props.operationalMetricTypes);
    const updateRenders = renderDrivers(update, accountsMap, props.operationalMetricTypes);

    return <div>
        {addRenders.length > 0 &&
                <div className={common.section}>
                    <div className={styles.operationHeader}>Additions</div>
                    {addRenders}
                </div>
        }
        {updateRenders.length > 0 &&
                <div className={common.section}>
                    <div className={styles.operationHeader}>Updates</div>
                    {updateRenders}
                </div>
        }
        {removeRenders.length > 0 &&
                <div className={common.section}>
                    <div className={styles.operationHeader}>Removals</div>
                    {removeRenders}
                </div>
        }
    </div>;
}